import { Box } from "@mui/material";
import { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { AccordionType, useStatistiquesContext, ViewMode } from "./statistiquesContext";
import { FilterAccordion, FilterAccordionItem } from "../../components/FilterAccordion/FilterAccordion";

const ACCORDION_WIDTH = "30vw";
const MIN_ACCORDION_WIDTH = 400;
const MAX_ACCORDION_WIDTH = 500;

export const StatistiquesFiltersAccordions: FunctionComponent = () => {
    const {
        currentView,
        selectedDivisions,
        selectedSections,
        selectedTypeProduitWeb,

        magasinAccordionItems,
        setMagasinAccordionItems,
        divisionAccordionItems,
        setDivisionAccordionItems,
        sectionAccordionItems,
        setSectionAccordionItems,
        typeProduitWebAccordionItems,
        setTypeProduitWebAccordionItems,
        produitAccordionItems,
        setProduitAccordionItems,

        productAccordionExpanded,
        setProductAccordionExpanded,
    } = useStatistiquesContext();

    const containerRef = useRef<HTMLDivElement>(null);
    const [containerHeight, setContainerHeight] = useState(0);
    const [headerHeights, setHeaderHeights] = useState<Record<string, number>>({});

    useEffect(() => {
        if (!containerRef.current) return;
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setContainerHeight(entry.contentRect.height);
            }
        });
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, []);

    const handleHeaderResize = useCallback((id: string, height: number) => {
        setHeaderHeights(prev => {
            if (prev[id] === height) return prev;
            return { ...prev, [id]: height };
        });
    }, []);

    const totalHeaderHeight = Object.values(headerHeights).reduce((a, b) => a + b, 0);
    const maxBodyHeight = containerHeight - totalHeaderHeight - 60;

    const onToggleMagasin = useCallback(() => {
        setProductAccordionExpanded(prev => prev === AccordionType.MAGASIN ? false : AccordionType.MAGASIN);
    }, [setProductAccordionExpanded]);

    const onToggleDivision = useCallback(() => {
        setProductAccordionExpanded(prev => prev === AccordionType.DIVISION ? false : AccordionType.DIVISION);
    }, [setProductAccordionExpanded]);

    const onToggleSection = useCallback(() => {
        setProductAccordionExpanded(prev => prev === AccordionType.SECTION ? false : AccordionType.SECTION);
    }, [setProductAccordionExpanded]);

    const onToggleTypeProduitWeb = useCallback(() => {
        setProductAccordionExpanded(prev => prev === AccordionType.TYPE_PRODUIT_WEB ? false : AccordionType.TYPE_PRODUIT_WEB);
    }, [setProductAccordionExpanded]);

    const onToggleProduit = useCallback(() => {
        setProductAccordionExpanded(prev => prev === AccordionType.PRODUIT ? false : AccordionType.PRODUIT);
    }, [setProductAccordionExpanded]);

    const onItemClickMagasin = useCallback((_itemId: any, items_: FilterAccordionItem<any>[]) => {
        setMagasinAccordionItems(items_);
    }, [setMagasinAccordionItems]);

    const onItemClickDivision = useCallback((_itemId: any, items_: FilterAccordionItem<any>[]) => {
        setDivisionAccordionItems(items_);
    }, [setDivisionAccordionItems]);

    const onItemClickSection = useCallback((_itemId: any, items_: FilterAccordionItem<any>[]) => {
        setSectionAccordionItems(items_);
    }, [setSectionAccordionItems]);

    const onItemClickTypeProduitWeb = useCallback((_itemId: any, items_: FilterAccordionItem<any>[]) => {
        setTypeProduitWebAccordionItems(items_);
    }, [setTypeProduitWebAccordionItems]);

    const onItemClickProduit = useCallback((_itemId: any, items_: FilterAccordionItem<any>[]) => {
        setProduitAccordionItems(items_);
    }, [setProduitAccordionItems]);

    const onHeaderHeightChangeMagasin = useCallback((size: number) => handleHeaderResize('magasins', size), [handleHeaderResize]);
    const onHeaderHeightChangeDivision = useCallback((size: number) => handleHeaderResize('division', size), [handleHeaderResize]);
    const onHeaderHeightChangeSection = useCallback((size: number) => handleHeaderResize('section', size), [handleHeaderResize]);
    const onHeaderHeightChangeTypeProduitWeb = useCallback((size: number) => handleHeaderResize('type_produit_web', size), [handleHeaderResize]);
    const onHeaderHeightChangeProduit = useCallback((size: number) => handleHeaderResize('produit', size), [handleHeaderResize]);

    return (
        <Box
            ref={containerRef}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: 'fit-content',
                boxSizing: 'border-box',
                p: 1,
                overflow: 'hidden',
                padding: 0,
                paddingRight: "4px",
            }}
        >
            {currentView === ViewMode.PRODUCT && (
                <FilterAccordion
                    title="Magasins"
                    width={ACCORDION_WIDTH}
                    minWidth={MIN_ACCORDION_WIDTH}
                    maxWidth={MAX_ACCORDION_WIDTH}
                    items={magasinAccordionItems}
                    expanded={productAccordionExpanded === AccordionType.MAGASIN}
                    allowMultipleSelection={true}
                    showSearch={true}
                    showCheckbox={true}
                    maxContentHeight={maxBodyHeight}
                    canSelectAll={true}
                    onToggle={onToggleMagasin}
                    onItemClick={onItemClickMagasin}
                    onHeaderHeightChange={onHeaderHeightChangeMagasin}
                />
            )}
            <FilterAccordion
                title="Divisions"
                width={ACCORDION_WIDTH}
                minWidth={MIN_ACCORDION_WIDTH}
                maxWidth={MAX_ACCORDION_WIDTH}
                items={divisionAccordionItems}
                expanded={productAccordionExpanded === AccordionType.DIVISION}
                allowMultipleSelection={true}
                showSearch={true}
                showCheckbox={true}
                disabled={!divisionAccordionItems.length} //{currentView === ViewMode.PRODUCT && !selectedMagasins.length}
                maxContentHeight={maxBodyHeight}
                canSelectAll={true}
                onToggle={onToggleDivision}
                onItemClick={onItemClickDivision}
                onHeaderHeightChange={onHeaderHeightChangeDivision}
            />

            <FilterAccordion
                title="Sections"
                width={ACCORDION_WIDTH}
                minWidth={MIN_ACCORDION_WIDTH}
                maxWidth={MAX_ACCORDION_WIDTH}
                items={sectionAccordionItems}
                expanded={productAccordionExpanded === AccordionType.SECTION}
                allowMultipleSelection={true}
                showSearch={true}
                showCheckbox={true}
                disabled={!selectedDivisions.length}
                maxContentHeight={maxBodyHeight}
                canSelectAll={true}
                onToggle={onToggleSection}
                onItemClick={onItemClickSection}
                onHeaderHeightChange={onHeaderHeightChangeSection}
            />

            <FilterAccordion
                title="Type de produits"
                width={ACCORDION_WIDTH}
                minWidth={MIN_ACCORDION_WIDTH}
                maxWidth={MAX_ACCORDION_WIDTH}
                items={typeProduitWebAccordionItems}
                expanded={productAccordionExpanded === AccordionType.TYPE_PRODUIT_WEB}
                allowMultipleSelection={true}
                showSearch={true}
                showCheckbox={true}
                disabled={!selectedSections.length}
                maxContentHeight={maxBodyHeight}
                canSelectAll={true}
                onToggle={onToggleTypeProduitWeb}
                onItemClick={onItemClickTypeProduitWeb}
                onHeaderHeightChange={onHeaderHeightChangeTypeProduitWeb}
            />

            {currentView === ViewMode.STORE && (
                <FilterAccordion
                    title="Produits"
                    width={ACCORDION_WIDTH}
                    minWidth={MIN_ACCORDION_WIDTH}
                    maxWidth={MAX_ACCORDION_WIDTH}
                    showSearch={true}
                    items={produitAccordionItems}
                    expanded={productAccordionExpanded === AccordionType.PRODUIT}
                    allowMultipleSelection={true}
                    showCheckbox={true}
                    disabled={!selectedTypeProduitWeb.length}
                    maxContentHeight={maxBodyHeight}
                    canSelectAll={true}
                    onToggle={onToggleProduit}
                    onItemClick={onItemClickProduit}
                    onHeaderHeightChange={onHeaderHeightChangeProduit}
                    maxSelectedItems={200}
                />
            )}
        </Box>
    );
};

export default StatistiquesFiltersAccordions;